import { Link } from "react-router-dom";
import { ArrowLeft, BookOpenCheck, ShieldAlert, HeartHandshake, Users, Scale } from "lucide-react";
import { CrisisFooter } from "@/components/sim/CrisisFooter";

const Section = ({ icon: Icon, title, children }) => (
  <section className="mt-8">
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5 text-emerald-700" />
      <h2 className="font-heading text-lg font-semibold text-stone-900">{title}</h2>
    </div>
    <div className="mt-3 space-y-3 text-base leading-relaxed text-stone-600">{children}</div>
  </section>
);

export default function SourcesPage() {
  return (
    <div data-testid="sources-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <Link to="/" data-testid="sources-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" /> Back to simulator
      </Link>

      <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800">About this training</p>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">Sources and Approach</h1>
      <p className="mt-3 text-base leading-relaxed text-stone-600">
        Safe Path Simulator is a practice space for domestic violence victim advocacy. Here is what the
        content is built on, in plain language.
      </p>

      <Section icon={HeartHandshake} title="Trauma-informed care principles">
        <p>
          Every case and every piece of feedback is grounded in the five core principles of trauma-informed
          care: <strong className="font-medium text-stone-800">safety</strong>,{" "}
          <strong className="font-medium text-stone-800">trustworthiness</strong>,{" "}
          <strong className="font-medium text-stone-800">choice</strong>,{" "}
          <strong className="font-medium text-stone-800">collaboration</strong>, and{" "}
          <strong className="font-medium text-stone-800">empowerment</strong>. In practice that means
          prioritizing what keeps a survivor safe, being honest about what you can and cannot do,
          protecting her right to decide, planning with her rather than for her, and building on her
          own strengths.
        </p>
      </Section>

      <Section icon={ShieldAlert} title="Lethality research">
        <p>
          The risk indicators woven through the cases reflect established lethality research. Prior
          non-fatal strangulation and an abuser's access to a firearm are treated as key signals that
          abuse may turn deadly, and the scenarios train you to notice them even when they are mentioned
          casually. Other recognized indicators, such as recent separation, escalation, and threats,
          appear throughout the cases as well.
        </p>
      </Section>

      <Section icon={BookOpenCheck} title="Survivor-led safety planning">
        <p>
          The simulation follows survivor-led safety planning practice: safety plans are built for
          staying, leaving, and everything in between, on the survivor's timeline. The trainee is
          rewarded for meeting people where they are, offering warm referrals, planning around
          technology and surveillance risks, and returning control to the person who lost it.
        </p>
      </Section>

      <Section icon={Users} title="A real-world pilot">
        <p>
          This content is being refined in partnership with practicing shelter counselors through a
          real-world pilot. Their feedback shapes the scenarios, the choices, and the coaching so that
          what you practice here matches what the work actually asks of you.
        </p>
      </Section>

      <Section icon={Scale} title="A note on law and policy">
        <p data-testid="legal-note">
          Confidentiality and mandatory reporting rules vary by state and by agency. Trainees must
          follow their own state law and their agency's policy. The scenarios in this simulation
          illustrate principles; they are not legal requirements, and nothing here replaces your
          agency's training or guidance.
        </p>
      </Section>

      <CrisisFooter className="mt-12" />
    </div>
  );
}
